import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useTheme, type ThemePref } from "@/hooks/use-theme";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LogOut, Monitor, Moon, Sun } from "lucide-react";

export const Route = createFileRoute("/_app/settings")({
  head: () => ({ meta: [{ title: "Settings — EESWEB" }] }),
  component: SettingsPage,
});

const THEME_OPTIONS: { value: ThemePref; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "Match device", icon: Monitor },
];

function SettingsPage() {
  const { user, role, clientName, clientId, signOut } = useAuth();
  const { preference, setPreference } = useTheme();

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Your account and how the panel looks on this device.</p>
      </div>

      <section className="rounded-2xl border bg-card shadow-soft">
        <h2 className="px-5 pt-5 text-sm font-semibold">Account</h2>
        <dl className="divide-y px-5 pb-2 text-sm">
          <Row label="Signed in as" value={user?.email ?? "—"} />
          <Row label="Access" value={role === "admin" ? "EESWEB admin (all dealerships)" : role === "client" ? "Dealership user" : "—"} />
          <Row label="Dealership" value={clientName ?? (role === "admin" ? "All dealerships" : "Not assigned yet")} />
          {clientId && role === "admin" && <Row label="Dealership ID" value={<span className="mono text-xs">{clientId}</span>} />}
        </dl>
        {!clientId && role !== "admin" && (
          <p className="mx-5 mb-5 rounded-xl bg-warning/10 px-4 py-3 text-sm text-foreground">
            Your account isn't linked to a dealership yet, so there are no calls to show. Ask your EESWEB contact to assign you.
          </p>
        )}
      </section>

      <section className="rounded-2xl border bg-card p-5 shadow-soft">
        <h2 className="text-sm font-semibold">Appearance</h2>
        <p className="mb-4 text-xs text-muted-foreground">Saved on this device only.</p>
        <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-2">
          {THEME_OPTIONS.map((o) => {
            const on = preference === o.value;
            return (
              <button
                key={o.value}
                role="radio"
                aria-checked={on}
                onClick={() => setPreference(o.value)}
                className={cn(
                  "flex flex-col items-center gap-2 rounded-xl border px-3 py-3.5 text-sm font-medium transition-colors",
                  on ? "border-primary bg-primary/5 text-primary" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                <o.icon className="size-5" />
                {o.label}
              </button>
            );
          })}
        </div>
      </section>

      <Button variant="outline" className="w-full justify-center gap-2 text-destructive hover:bg-destructive/5 hover:text-destructive sm:w-auto" onClick={() => signOut()}>
        <LogOut className="size-4" /> Sign out
      </Button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 py-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate font-medium">{value}</dd>
    </div>
  );
}
